import dayjs from "dayjs";
import utc from "dayjs/plugin/utc.js";
import timezone from "dayjs/plugin/timezone.js";
import isToday from "dayjs/plugin/isToday.js";
import isTomorrow from "dayjs/plugin/isTomorrow.js";
import localizedFormat from "dayjs/plugin/localizedFormat.js";
import advancedFormat from "dayjs/plugin/advancedFormat.js";
import { useLocale } from "../use-locale";

/* eslint-disable import/no-named-as-default-member */
dayjs.extend(utc);
dayjs.extend(isTomorrow);
dayjs.extend(isToday);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);
dayjs.extend(advancedFormat);
/* eslint-enable import/no-named-as-default-member */

export const useDate = () => {
  const { t } = useLocale();

  const getReadableDate = (
    date: string,
    dayjsOutputFormat?: string,
    dayjsInputFormat?: string,
  ) => {
    const inputDate = dayjs(date, dayjsInputFormat);

    let readableDate: string;

    if (inputDate.isToday()) {
      readableDate = t("composables.use_date.today");
    } else if (inputDate.isTomorrow()) {
      readableDate = t("composables.use_date.tomorrow");
    } else {
      readableDate = inputDate.format(dayjsOutputFormat);
    }

    return readableDate;
  };

  const formatDatetime = (date: string, dayjsOutputFormat?: string) => {
    return dayjs(date).format(dayjsOutputFormat);
  };

  const formatTimeRange = (timeRange: string, dayjsFormat?: string) => {
    const timeFormat = dayjsFormat || getTimeFormat();

    const [start, end] = timeRange.split("-").map((time) => {
      const [hours, minutes] = time.split(":");
      const now = dayjs()
        .startOf("day")
        .set("hours", +hours)
        .set("minutes", +minutes);

      return now;
    });

    const from = start.format(timeFormat);
    const to = end.format(timeFormat);

    return t("composables.use_date.time_range", {
      from,
      to,
    });
  };

  const formatTime = (time: string, dayjsFormat?: string) => {
    const timeFormat = dayjsFormat || getTimeFormat();

    const [hours, minutes] = time.split(":");

    return dayjs()
      .startOf("day")
      .set("hours", +hours)
      .set("minutes", +minutes)
      .format(timeFormat);
  };

  const isOlderThan = (
    date: Date | string,
    time: number,
    type: "hours" | "minutes" | "seconds",
  ): boolean => {
    const givenDate = dayjs(date);

    const secsDiff = dayjs().diff(givenDate, "seconds");

    if (type === "hours") {
      return secsDiff / 3600 > time;
    }

    if (type === "minutes") {
      return secsDiff / 60 > time;
    }

    return secsDiff > time;
  };

  const getTimeFormat = (locale = "default") => {
    const formattedTime = new Intl.DateTimeFormat(locale, {
      hour: "numeric",
    }).format(new Date(2024, 0, 1, 13, 0));

    return formattedTime.includes("13") ? "H:mm" : "h:mma";
  };

  return {
    getReadableDate,
    formatDatetime,
    formatTimeRange,
    formatTime,
    isOlderThan,
    getTimeFormat,
  };
};
